import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from 'v2/components/Button';

import MoreActionsIcon from 'components/icons/MoreActionsIcon';

import {
    ESCAPE_KEY,
    MENU_OFFSET_PIXELS,
    MENU_TEST_ID,
    TEST_ID,
    getEstimatedMenuHeightPixels
} from './constants';
import type { FunctionComponent, MenuPosition, Props, ReactMouseEvent } from './types';

import styles from './styles.module.scss';

const RowActionsMenu: FunctionComponent<Props> = ({
    actions,
    ariaLabel,
    className = '',
    testId = TEST_ID
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

    const closeMenu = useCallback(() => setMenuPosition(null), []);

    useEffect(() => {
        if (!menuPosition) {
            return undefined;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;

            if (
                target &&
                (triggerRef.current?.contains(target) || menuRef.current?.contains(target))
            ) {
                return;
            }

            closeMenu();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === ESCAPE_KEY) {
                closeMenu();
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', closeMenu);
        window.addEventListener('scroll', closeMenu, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', closeMenu);
            window.removeEventListener('scroll', closeMenu, true);
        };
    }, [closeMenu, menuPosition]);

    const handleToggle = useCallback(
        (event: ReactMouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();

            if (menuPosition) {
                closeMenu();

                return;
            }

            const triggerBounds = triggerRef.current?.getBoundingClientRect();

            if (!triggerBounds) {
                return;
            }

            const estimatedMenuHeight = getEstimatedMenuHeightPixels(actions.length);
            const hasRoomBelow = triggerBounds.bottom + estimatedMenuHeight < window.innerHeight;

            setMenuPosition({
                right: window.innerWidth - triggerBounds.right,
                top: hasRoomBelow
                    ? triggerBounds.bottom + MENU_OFFSET_PIXELS
                    : triggerBounds.top - estimatedMenuHeight - MENU_OFFSET_PIXELS
            });
        },
        [actions.length, closeMenu, menuPosition]
    );

    const handleActionClick = useCallback(
        (event: ReactMouseEvent<HTMLButtonElement>, onClick: () => void) => {
            event.stopPropagation();
            closeMenu();
            onClick();
        },
        [closeMenu]
    );

    return (
        <div className={`${className} ${styles.rowActionsMenu}`} ref={triggerRef}>
            <Button
                ariaExpanded={Boolean(menuPosition)}
                ariaLabel={ariaLabel}
                onClick={handleToggle}
                testId={testId}
            >
                <MoreActionsIcon className={styles.icon} />
            </Button>

            {menuPosition
                ? createPortal(
                      <div
                          className={styles.menu}
                          data-testid={MENU_TEST_ID}
                          ref={menuRef}
                          style={{ right: menuPosition.right, top: menuPosition.top }}
                      >
                          {actions.map(action => (
                              <Button
                                  className={`${styles.menuItem}${action.isDestructive ? ` ${styles.destructive}` : ''}`}
                                  key={action.label}
                                  onClick={event => handleActionClick(event, action.onClick)}
                              >
                                  {action.label}
                              </Button>
                          ))}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
};

export default RowActionsMenu;
